import { Stack, Typography } from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import React from "react";

const OUTLINE_RUBRIC_URL =
    "https://www.authenticxhelp.com/en/articles/13927025-conversation-outline-model";

const outlineDefinitions = [
    {
        title: "Greeting",
        description:
            "The opening of the call, including introductions, company or program names, and initial offers to help before identity verification or discussion of the issue begins.",
    },
    {
        title: "Verification",
        description:
            "Identity, HIPAA, or consent checks (such as confirming date of birth, address, or member ID) required before discussing account-specific or sensitive information.",
    },
    {
        title: "Problem Statement",
        description:
            "A clear summary of why the caller reached out, describing the main question, issue, or need before troubleshooting or investigation begins.",
    },
    {
        title: "Clarification",
        description:
            "Additional information gathering or review to better understand the issue. This may include follow-up questions, reviewing details, or confirming previously shared information to determine the best path forward.",
    },
    {
        title: "Solution Provided",
        description:
            "The point in the call where a clear resolution, next step, or actionable guidance is delivered to address the caller's need.",
    },
    {
        title: "Transfer/Hold",
        description:
            "When the caller is placed on hold or transferred to another person or department.",
    },
    {
        title: "Closing",
        description:
            "The wrap-up of the call, including summaries, confirmation of next steps, satisfaction checks, or professional sign-offs.",
    },
    {
        title: "IVR",
        description:
            "Interactions with an automated phone system, such as menu prompts, routing options, authentication steps, or automated surveys.",
    },
    {
        title: "Voicemail",
        description:
            "A recorded message left when only one party is speaking and no live conversation takes place.",
    },
];

const OutlineDefinitionsContent: React.FC = () => {
    return (
        <Stack sx={{ gap: 2 }}>
            {outlineDefinitions.map((definition) => (
                <Stack
                    key={definition.title}
                    sx={{ color: "neutral.800", gap: 0.5 }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {definition.title}
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                        {definition.description}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};

interface OutlineDefinitionsDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const OutlineDefinitionsDialog: React.FC<OutlineDefinitionsDialogProps> = ({
    isOpen,
    onClose,
}) => {
    return (
        <AcxDialog
            title="Outline Definitions"
            subTitle="Each segment is defined with clear criteria to ensure transparency in how call insights are generated."
            isOpen={isOpen}
            maxWidth="md"
            onClose={onClose}
            dialogContentChildren={<OutlineDefinitionsContent />}
            dialogContentSx={{ maxHeight: "55vh" }}
            hideCancelButton
            id="segment-outline-definitions"
        >
            <Stack direction="row" justifyContent="end" gap={1}>
                <AcxButton color="white" onClick={onClose}>
                    Close
                </AcxButton>
                <AcxButton
                    onClick={() => {
                        window.open(
                            OUTLINE_RUBRIC_URL,
                            "_blank",
                            "noopener,noreferrer",
                        );
                        onClose();
                    }}
                    endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                >
                    View Rubric
                </AcxButton>
            </Stack>
        </AcxDialog>
    );
};

export default OutlineDefinitionsDialog;
